/**
 * AffiliateDashboardPage — Dashboard voor approved affiliates (P3).
 * Shows their promo codes, commissions, performance.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Copy, TrendingUp, Coins, Users, Loader2, RefreshCw } from 'lucide-react';

const formatEUR = (n) => `€${(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AffiliateDashboardPage() {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', '/api/affiliate/me');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Laden mislukt');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div style={{ padding: 60, textAlign: 'center' }}><Loader2 className="animate-spin" /></div>;

  const s = data.summary;

  return (
    <div data-testid="affiliate-dashboard">
      <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p style={{ fontSize: 11, color: '#D4A017', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <Sparkles size={11} style={{ display: 'inline', marginRight: 4 }} /> Affiliate
          </p>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 26, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
            Welkom, {data.user?.name?.split(' ')[0] || 'partner'}
          </h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>20% commissie van eerste maand bij elke conversie</p>
        </div>
        <button onClick={load} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Conversies" value={s.conversions} color="#0f172a" icon={Users} testid="aff-conversions" />
        <Kpi label="Code-gebruik" value={s.total_code_usage} color="#0f172a" icon={Sparkles} testid="aff-code-usage" />
        <Kpi label="Open commissie" value={formatEUR(s.open_ex_vat)} color="#D4A017" icon={Coins} testid="aff-open" />
        <Kpi label="Uitbetaald" value={formatEUR(s.paid_ex_vat)} color="#16a34a" icon={TrendingUp} testid="aff-paid" />
      </div>

      {/* Promo codes */}
      <div style={{ background: 'white', borderRadius: 16, padding: '20px 22px', border: '1px solid #f1f5f9', marginBottom: 16 }}>
        <p style={{ fontSize: 11, color: '#D4A017', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Mijn promo-links</p>
        {data.promo_codes?.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: 13 }}>Nog geen promo-link. Vraag de admin.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.promo_codes.map((c) => (
              <div key={c.id} data-testid={`aff-code-${c.id}`} style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <code style={{ background: '#D4A017', color: '#0f172a', padding: '3px 8px', borderRadius: 4, fontFamily: 'monospace', fontWeight: 800, fontSize: 13 }}>{c.code}</code>
                  <p style={{ fontSize: 12, color: '#92400e', marginTop: 4 }}>{c.discount_value}% korting voor klant · {c.current_usage}/{c.max_usage} gebruikt</p>
                  <p style={{ fontSize: 11, color: '#78350f', fontFamily: 'monospace', marginTop: 2, wordBreak: 'break-all' }}>{c.promo_url}</p>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(c.promo_url); toast.success('URL gekopieerd'); }} style={{ background: '#0f172a', color: 'white', padding: '6px 12px', borderRadius: 6, border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} data-testid={`copy-aff-${c.id}`}>
                  <Copy size={12} /> Kopieer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Commissies */}
      <div style={{ background: 'white', borderRadius: 16, padding: '20px 22px', border: '1px solid #f1f5f9' }}>
        <p style={{ fontSize: 11, color: '#D4A017', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Mijn commissies</p>
        {data.commissions?.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: 13 }}>Nog geen conversies — deel uw promo-link!</p>
        ) : (
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '8px 10px', fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Datum</th>
                <th style={{ padding: '8px 10px', fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Klant</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Bedrag</th>
                <th style={{ padding: '8px 10px', fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.commissions.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid #f1f5f9' }} data-testid={`aff-commission-${c.id}`}>
                  <td style={{ padding: '8px 10px', fontSize: 12, color: '#64748b' }}>{c.created_at?.slice(0, 10)}</td>
                  <td style={{ padding: '8px 10px' }}>{c.klant_id?.slice(0, 8)}…</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{formatEUR(c.amount_ex_vat)}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: c.status === 'paid' ? '#d1fae5' : '#fef3c7', color: c.status === 'paid' ? '#047857' : '#92400e' }}>
                      {c.status === 'paid' ? 'Uitbetaald' : 'Open'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function Kpi({ label, value, color, icon: Icon, testid }) {
  return (
    <div data-testid={testid} style={{ background: 'white', borderRadius: 14, padding: '16px 18px', border: '1px solid #f1f5f9' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        <Icon size={12} style={{ color }} />
        <p style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{label}</p>
      </div>
      <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 24, fontWeight: 800, color }}>{value}</p>
    </div>
  );
}
